/**
 * Dynamic date-fns locale loader
 *
 * Instead of bundling all 62 locales (~330KB), this loads only the needed
 * locale on demand. The locale is cached after first load.
 */

import type { Locale } from 'date-fns';

// Map i18n language codes to date-fns locale import paths
const localeMap: Record<string, () => Promise<{ default: Locale }>> = {
  af: () => import('date-fns/locale/af'),
  sq: () => import('date-fns/locale/sq'),
  ar: () => import('date-fns/locale/ar'),
  hy: () => import('date-fns/locale/hy'),
  az: () => import('date-fns/locale/az'),
  bn: () => import('date-fns/locale/bn'),
  bs: () => import('date-fns/locale/bs'),
  bg: () => import('date-fns/locale/bg'),
  ca: () => import('date-fns/locale/ca'),
  zh: () => import('date-fns/locale/zh-CN'),
  zhTW: () => import('date-fns/locale/zh-TW'),
  hr: () => import('date-fns/locale/hr'),
  cs: () => import('date-fns/locale/cs'),
  da: () => import('date-fns/locale/da'),
  nl: () => import('date-fns/locale/nl'),
  et: () => import('date-fns/locale/et'),
  fa: () => import('date-fns/locale/fa-IR'),
  faAF: () => import('date-fns/locale/fa-IR'),
  fi: () => import('date-fns/locale/fi'),
  fr: () => import('date-fns/locale/fr'),
  frCA: () => import('date-fns/locale/fr-CA'),
  ka: () => import('date-fns/locale/ka'),
  de: () => import('date-fns/locale/de'),
  el: () => import('date-fns/locale/el'),
  gu: () => import('date-fns/locale/gu'),
  ht: () => import('date-fns/locale/ht'),
  he: () => import('date-fns/locale/he'),
  hi: () => import('date-fns/locale/hi'),
  hu: () => import('date-fns/locale/hu'),
  is: () => import('date-fns/locale/is'),
  id: () => import('date-fns/locale/id'),
  ga: () => import('date-fns/locale/en-IE'),
  it: () => import('date-fns/locale/it'),
  ja: () => import('date-fns/locale/ja'),
  kn: () => import('date-fns/locale/kn'),
  kk: () => import('date-fns/locale/kk'),
  ko: () => import('date-fns/locale/ko'),
  lv: () => import('date-fns/locale/lv'),
  lt: () => import('date-fns/locale/lt'),
  mk: () => import('date-fns/locale/mk'),
  ms: () => import('date-fns/locale/ms'),
  ml: () => import('date-fns/locale/ta'), // Malayalam fallback to Tamil
  mt: () => import('date-fns/locale/mt'),
  mn: () => import('date-fns/locale/mn'),
  no: () => import('date-fns/locale/nb'),
  nb: () => import('date-fns/locale/nb'),
  ps: () => import('date-fns/locale/fa-IR'), // Pashto fallback to Farsi
  pl: () => import('date-fns/locale/pl'),
  pt: () => import('date-fns/locale/pt-BR'),
  ptPT: () => import('date-fns/locale/pt'),
  ptBR: () => import('date-fns/locale/pt-BR'),
  pa: () => import('date-fns/locale/hi'), // Punjabi fallback to Hindi
  ro: () => import('date-fns/locale/ro'),
  ru: () => import('date-fns/locale/ru'),
  sr: () => import('date-fns/locale/sr'),
  si: () => import('date-fns/locale/bn'), // Sinhala fallback to Bengali
  sk: () => import('date-fns/locale/sk'),
  sl: () => import('date-fns/locale/sl'),
  es: () => import('date-fns/locale/es'),
  esMX: () => import('date-fns/locale/es'),
  sv: () => import('date-fns/locale/sv'),
  ta: () => import('date-fns/locale/ta'),
  te: () => import('date-fns/locale/te'),
  th: () => import('date-fns/locale/th'),
  tr: () => import('date-fns/locale/tr'),
  uk: () => import('date-fns/locale/uk'),
  ur: () => import('date-fns/locale/hi'), // Urdu fallback to Hindi
  uz: () => import('date-fns/locale/uz'),
  vi: () => import('date-fns/locale/vi'),
  cy: () => import('date-fns/locale/cy'),
};

// Cache for loaded locales
const localeCache: Record<string, Locale> = {};

// Pending promises to avoid duplicate loads
const pendingLoads: Record<string, Promise<Locale | undefined>> = {};

/**
 * Get a cached locale synchronously (returns undefined if not yet loaded)
 */
export const getLocale = (langCode: string): Locale | undefined => {
  return localeCache[langCode];
};

/**
 * Load a locale asynchronously and cache it
 */
export const loadLocale = async (langCode: string): Promise<Locale | undefined> => {
  // Return cached if available
  if (localeCache[langCode]) {
    return localeCache[langCode];
  }

  // Return pending promise if already loading
  if (langCode in pendingLoads) {
    return pendingLoads[langCode];
  }

  const loader = localeMap[langCode];
  if (!loader) {
    return undefined;
  }

  // Start loading and cache the promise
  pendingLoads[langCode] = loader()
    .then((module) => {
      localeCache[langCode] = module.default;
      delete pendingLoads[langCode];
      return module.default;
    })
    .catch(() => {
      delete pendingLoads[langCode];
      return undefined;
    });

  return pendingLoads[langCode];
};

/**
 * Proxy object for backward compatibility with existing code
 * Returns cached locale or undefined (date-fns will use default formatting)
 */
const locales: Record<string, Locale | undefined> = new Proxy({} as Record<string, Locale | undefined>, {
  get: (_target, prop: string) => {
    // Return cached locale if available
    if (localeCache[prop]) {
      return localeCache[prop];
    }
    // Trigger async load (next render will have it)
    if (localeMap[prop]) {
      loadLocale(prop);
    }
    // Return undefined for now - date-fns will use default format
    return undefined;
  },
});

export default locales;
